import { useState } from "react";
import { track } from "@/lib/api";
import { US_STATES } from "@/data/siteContent";
import { useLeadSubmit, ConsentCheckbox, Honeypot, FieldError } from "@/components/forms/shared";

const COVERAGE_TYPES = ["Final expense", "Term life", "Whole life", "Not sure yet"];

export default function CompactQuoteForm({ title = "Request Your Free Quote" }) {
  const { submit, submitting } = useLeadSubmit("quote");
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "", state: "", zip: "", coverage_type: "",
  });
  const [consent, setConsent] = useState(false);
  const [company, setCompany] = useState("");
  const [errors, setErrors] = useState({});
  const [started, setStarted] = useState(false);

  const set = (k) => (e) => {
    if (!started) { setStarted(true); track("quote_form_start", { form: "compact" }); }
    setForm({ ...form, [k]: e.target.value });
    setErrors((p) => ({ ...p, [k]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Please enter your first name.";
    if (!form.last_name.trim()) e.last_name = "Please enter your last name.";
    if (!form.phone.trim()) e.phone = "Please enter your phone number.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (!form.state) e.state = "Please select your state.";
    if (!form.zip.trim()) e.zip = "Please enter your ZIP code.";
    if (!form.coverage_type) e.coverage_type = "Please choose a coverage type.";
    if (!consent) e.consent = "Please confirm your consent so we may contact you.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  return (
    <form
      className="relative rounded-2xl border border-navy-100 bg-white p-6 sm:p-8 shadow-lg"
      onSubmit={(e) => { e.preventDefault(); if (validate()) submit(form, { consent, company }); }}
      noValidate
      data-testid="compact-quote-form"
    >
      <Honeypot value={company} onChange={setCompany} />
      <h2 className="text-2xl tracking-tight">{title}</h2>
      <p className="mt-1.5 text-mutedink">Takes about a minute. No cost, no obligation.</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="cqf-first">First name</label>
          <input id="cqf-first" className="field-input" autoComplete="given-name" value={form.first_name} onChange={set("first_name")}
            aria-invalid={!!errors.first_name} data-testid="compact-first-name" />
          <FieldError message={errors.first_name} />
        </div>
        <div>
          <label className="field-label" htmlFor="cqf-last">Last name</label>
          <input id="cqf-last" className="field-input" autoComplete="family-name" value={form.last_name} onChange={set("last_name")}
            aria-invalid={!!errors.last_name} data-testid="compact-last-name" />
          <FieldError message={errors.last_name} />
        </div>
        <div>
          <label className="field-label" htmlFor="cqf-phone">Phone number</label>
          <input id="cqf-phone" type="tel" className="field-input" autoComplete="tel" value={form.phone} onChange={set("phone")}
            aria-invalid={!!errors.phone} data-testid="compact-phone" />
          <FieldError message={errors.phone} />
        </div>
        <div>
          <label className="field-label" htmlFor="cqf-email">Email address</label>
          <input id="cqf-email" type="email" className="field-input" autoComplete="email" value={form.email} onChange={set("email")}
            aria-invalid={!!errors.email} data-testid="compact-email" />
          <FieldError message={errors.email} />
        </div>
        <div>
          <label className="field-label" htmlFor="cqf-state">State</label>
          <select id="cqf-state" className="field-input" value={form.state} onChange={set("state")}
            aria-invalid={!!errors.state} data-testid="compact-state">
            <option value="">Select state</option>
            {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <FieldError message={errors.state} />
        </div>
        <div>
          <label className="field-label" htmlFor="cqf-zip">ZIP code</label>
          <input id="cqf-zip" className="field-input" inputMode="numeric" autoComplete="postal-code" value={form.zip} onChange={set("zip")}
            aria-invalid={!!errors.zip} data-testid="compact-zip" />
          <FieldError message={errors.zip} />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label" htmlFor="cqf-type">Type of coverage</label>
          <select id="cqf-type" className="field-input" value={form.coverage_type} onChange={set("coverage_type")}
            aria-invalid={!!errors.coverage_type} data-testid="compact-coverage-type">
            <option value="">What are you looking for?</option>
            {COVERAGE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <FieldError message={errors.coverage_type} />
        </div>
      </div>

      <div className="mt-5">
        <ConsentCheckbox checked={consent} onChange={(v) => { setConsent(v); setErrors((p) => ({ ...p, consent: "" })); }}
          error={errors.consent} testid="compact" />
      </div>

      <button type="submit" className="btn-accent mt-6 w-full" disabled={submitting} data-testid="compact-submit-button">
        {submitting ? "Sending…" : "Get My Free Quote"}
      </button>
      <p className="mt-3 text-center text-sm text-mutedink">
        Preliminary quote assistance only — this is not an insurance application.
      </p>
    </form>
  );
}
